import { useState, useCallback } from 'react';
import axios from 'axios';
import api from '../services/api';
import {
  RunStockScanResult,
  GetLatestScanResultsResponse,
  ScanResultDto,
} from '../types/api';

function toFriendlyError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err) && !err.response) {
    return err.code === 'ECONNABORTED'
      ? 'The request timed out. A full scan can take a few minutes — please try again.'
      : `Cannot reach the backend server at ${api.getBaseUrl()}.`;
  }
  return err instanceof Error ? err.message : fallback;
}

export interface ScanState {
  isLoading: boolean;
  error: string | null;
  lastScan: RunStockScanResult | null;
  results: ScanResultDto[];
  stats: {
    totalProcessed: number;
    inCriteria: number;
    gettingBetter: number;
    fallingAway: number;
  };
}

const initialState: ScanState = {
  isLoading: false,
  error: null,
  lastScan: null,
  results: [],
  stats: {
    totalProcessed: 0,
    inCriteria: 0,
    gettingBetter: 0,
    fallingAway: 0,
  },
};

/**
 * Hook for managing stock scan state and operations
 */
export function useScanState() {
  const [state, setState] = useState<ScanState>(initialState);

  const runScan = useCallback(async () => {
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const result = await api.runScan();
      setState(prev => ({
        ...prev,
        lastScan: result,
        stats: {
          totalProcessed: result.totalSymbolsProcessed,
          inCriteria: result.symbolsInCriteria,
          gettingBetter: result.symbolsGettingBetter,
          fallingAway: result.symbolsFallingAway,
        },
        error: null,
      }));

      // Fetch latest results
      await fetchLatestResults();
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: toFriendlyError(err, 'Failed to run scan'),
      }));
    } finally {
      setState(prev => ({
        ...prev,
        isLoading: false,
      }));
    }
  }, []);

  const fetchLatestResults = useCallback(async (classification?: string) => {
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const data = await api.getLatestResults(classification);
      const scanRun = data.scanRun;

      setState(prev => ({
        ...prev,
        results: data.results,
        lastScan: {
          scanRunId: scanRun.id,
          scanTimestamp: scanRun.scanTimestamp,
          totalSymbolsProcessed: scanRun.totalSymbolsProcessed,
          symbolsInCriteria: scanRun.symbolsInCriteria,
          symbolsGettingBetter: scanRun.symbolsGettingBetter,
          symbolsFallingAway: scanRun.symbolsFallingAway,
          topInCriteriaResults: data.results.filter(
            r => r.classification === 'BUY' || r.classification === 'HOLD'
          ),
        },
        stats: {
          totalProcessed: scanRun.totalSymbolsProcessed,
          inCriteria: scanRun.symbolsInCriteria,
          gettingBetter: scanRun.symbolsGettingBetter,
          fallingAway: scanRun.symbolsFallingAway,
        },
        error: null,
      }));
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: toFriendlyError(err, 'Failed to fetch results'),
      }));
    } finally {
      setState(prev => ({
        ...prev,
        isLoading: false,
      }));
    }
  }, []);

  const resetState = useCallback(() => {
    setState(initialState);
  }, []);

  return {
    ...state,
    runScan,
    fetchLatestResults,
    resetState,
  };
}
